import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
// import { OrbitControls, useHelper } from '@react-three/drei';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

function CameraRig({ target, orbitMode }) {
  const { camera } = useThree();
  const offset = new THREE.Vector3(0, 3, 8); // Increased Z offset for better following

  useFrame(() => {
    if (orbitMode || !target.current) return;

    // Get car's current position and rotation
    const carPosition = target.current.position;
    const carRotation = target.current.rotation;

    // Calculate camera position behind the car
    const cameraPosition = new THREE.Vector3();
    cameraPosition.copy(offset);
    cameraPosition.applyAxisAngle(new THREE.Vector3(0, 1, 0), carRotation.y);
    cameraPosition.add(carPosition);

    // Smooth camera movement
    camera.position.lerp(cameraPosition, 0.1);

    // Look at a point slightly above the car
    const lookAtPosition = new THREE.Vector3();
    lookAtPosition.copy(carPosition);
    lookAtPosition.y += 0.5;
    camera.lookAt(lookAtPosition);
  });

  return null;
}

function GuyHead({ parentRef }) {
  const headRef = useRef();
  const groupRef = useRef();
  const springRef = useRef({
    position: new THREE.Vector3(0, 0.5, 0),  // Adjusted for new parent position
    velocity: new THREE.Vector3(0, 0, 0),
    rotation: new THREE.Euler(0, 0, 0),
    rotationVelocity: new THREE.Euler(0, 0, 0)
  });
  
  const lastPos = useRef(new THREE.Vector3());
  const lastRot = useRef(new THREE.Euler());

  // Initialize last position and rotation
  useEffect(() => {
    if (parentRef.current) {
      lastPos.current.copy(parentRef.current.position);
      lastRot.current.copy(parentRef.current.rotation);
      // Set initial group position
      groupRef.current.position.set(0, 0.5, 0);
    }
  }, [parentRef]);

  useFrame((state, delta) => {
    if (!groupRef.current || !parentRef.current) return;

    // Spring constants
    const springStrength = 80.0;
    const damping = 4.0;

    // Calculate car's movement
    const currentPos = parentRef.current.position.clone();
    const currentRot = parentRef.current.rotation.clone();
    const velocity = currentPos.sub(lastPos.current).divideScalar(delta);
    
    // Apply forces based on car movement
    springRef.current.velocity.x -= velocity.x * 0.5;
    springRef.current.velocity.z -= velocity.z * 0.5;
    
    // Apply turning forces
    const rotDiff = currentRot.y - lastRot.current.y;
    springRef.current.velocity.x -= rotDiff * 2;
    
    // Update spring physics
    const spring = springRef.current;
    
    // Position spring physics
    spring.position.add(spring.velocity.multiplyScalar(delta));
    const force = spring.position.clone().multiplyScalar(-springStrength);
    spring.velocity.add(force.multiplyScalar(delta));
    spring.velocity.multiplyScalar(1 / (1 + damping * delta));

    // Apply to head with proper initial offset
    groupRef.current.position.set(
      0 + spring.position.x,
      0.5 + spring.position.y,
      0 + spring.position.z
    );

    // Rotation spring physics
    const targetRotation = new THREE.Euler(
      -velocity.z * 0.1,
      -rotDiff * 2,
      -velocity.x * 0.1
    );

    groupRef.current.rotation.set(
      targetRotation.x + spring.position.z * 0.5,
      targetRotation.y,
      targetRotation.z - spring.position.x * 0.5
    );

    // Store last position and rotation
    lastPos.current.copy(parentRef.current.position);
    lastRot.current.copy(parentRef.current.rotation);
  });

  return (
    <group ref={groupRef}>
      {/* Add point light for the head */}
      <pointLight 
        position={[0, 0.5, 0]} 
        intensity={0.5} 
        distance={2} 
        decay={2}
      />
      
      {/* Head */}
      <mesh ref={headRef}>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial 
          color="#FFD700" 
          roughness={0.3} 
          metalness={0.2}
          emissive="#FFD700"
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Sunglasses */}
      <mesh position={[0, 0.05, 0.2]}>
        <boxGeometry args={[0.5, 0.12, 0.1]} />
        <meshStandardMaterial color="#000000" roughness={0.1} metalness={0.8} />
      </mesh>

      {/* Spiky Hair */}
      <group position={[0, 0.2, 0]}>
        {[-0.2, -0.1, 0, 0.1, 0.2].map((x, i) => (
          <mesh key={i} position={[x, 0.1, 0]} rotation={[0.3, 0, x * 0.5]}>
            <coneGeometry args={[0.08, 0.2, 4]} />
            <meshStandardMaterial color="#FFFF00" roughness={0.6} metalness={0.1} />
          </mesh>
        ))}
      </group>

      {/* Goatee */}
      <mesh position={[0, -0.2, 0.15]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.15, 0.2, 8]} />
        <meshStandardMaterial color="#808080" roughness={0.7} metalness={0.2} />
      </mesh>
    </group>
  );
}

const Car = forwardRef(({ position, rotation }, ref) => {
  const keys = useRef({ up: false, down: false, left: false, right: false });
  const forward = new THREE.Vector3(0, 0, -1);
  const tempVector = new THREE.Vector3();

  useFrame(() => {
    if (!ref.current) return;

    const rotationSpeed = 0.03;
    const moveSpeed = 0.4;

    // Handle rotation
    if (keys.current.left) ref.current.rotation.y += rotationSpeed;
    if (keys.current.right) ref.current.rotation.y -= rotationSpeed;

    // Calculate movement direction
    if (keys.current.up || keys.current.down) {
      // Get the forward direction based on car's current rotation
      tempVector.copy(forward);
      tempVector.applyEuler(ref.current.rotation);
      
      // Scale by speed and direction (forward or backward)
      const direction = keys.current.up ? 1 : -1;
      tempVector.multiplyScalar(moveSpeed * direction);
      
      // Apply movement
      ref.current.position.add(tempVector);
    }
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'w' || e.key === 'ArrowUp') keys.current.up = true;
      if (e.key === 's' || e.key === 'ArrowDown') keys.current.down = true;
      if (e.key === 'a' || e.key === 'ArrowLeft') keys.current.left = true;
      if (e.key === 'd' || e.key === 'ArrowRight') keys.current.right = true;
    };

    const handleKeyUp = (e) => {
      if (e.key === 'w' || e.key === 'ArrowUp') keys.current.up = false;
      if (e.key === 's' || e.key === 'ArrowDown') keys.current.down = false;
      if (e.key === 'a' || e.key === 'ArrowLeft') keys.current.left = false;
      if (e.key === 'd' || e.key === 'ArrowRight') keys.current.right = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return (
    <group ref={ref} position={position} rotation={rotation}>
      {/* Car body */}
      <group>
        {/* Main body */}
        <mesh position={[0, 0.3, 0]}>
          <boxGeometry args={[2, 0.4, 4]} />
          <meshStandardMaterial color="#FF0000" metalness={0.6} roughness={0.4} />
        </mesh>

        {/* Hood */}
        <mesh position={[0, 0.3, -1.2]}>
          <boxGeometry args={[2, 0.2, 1.6]} />
          <meshStandardMaterial color="#FF0000" metalness={0.6} roughness={0.4} />
        </mesh>

        {/* Cabin */}
        <mesh position={[0, 0.6, 0.4]}>
          <boxGeometry args={[1.6, 0.4, 1.2]} />
          <meshStandardMaterial color="#000000" metalness={0.7} roughness={0.3} />
        </mesh>

        {/* Wheels */}
        <mesh position={[0.8, 0, -1.2]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
        <mesh position={[-0.8, 0, -1.2]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
        <mesh position={[0.8, 0, 1.2]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
        <mesh position={[-0.8, 0, 1.2]}>
          <cylinderGeometry args={[0.3, 0.3, 0.2, 16]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
      </group>

      {/* Position the head relative to the car's cabin */}
      <group position={[0, 0.6, 0.4]}>
        <GuyHead parentRef={ref} />
      </group>
    </group>
  );
});

export default function GuyFerrari() {
  const [orbitMode, setOrbitMode] = useState(false);
  const carRef = useRef();

  return (
    <div style={{ height: '100vh', width: '100vw' }}>
      <div style={{ 
        position: 'absolute', 
        top: 20, 
        left: 20, 
        zIndex: 1,
        background: 'rgba(0,0,0,0.7)',
        padding: '1rem',
        borderRadius: '8px',
        color: 'white'
      }}>
        <h1>Guy Ferrari: Driving Simulator</h1>
        <p>Use WASD or arrow keys to drive</p>
        <button 
          onClick={() => setOrbitMode(!orbitMode)}
          style={{
            padding: '8px 16px',
            background: '#444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {orbitMode ? 'Switch to Follow Camera' : 'Switch to Orbit Camera'}
        </button>
      </div>

      <Canvas 
        camera={{ position: [0, 3, 8], fov: 60 }}
        style={{ background: '#e0e0e0' }}
      >
        <ambientLight intensity={0.7} />
        <pointLight position={[10, 10, 10]} intensity={0.8} />
        <hemisphereLight 
          skyColor="#ffffff"
          groundColor="#e0e0e0"
          intensity={0.3}
        />
        
        {/* Create two overlapping planes with different browns for a simple striped effect */}
        <group rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]}>
          {/* Base lighter brown */}
          <mesh>
            <planeGeometry args={[15, 200]} />
            <meshStandardMaterial color="#D4B08C" />
          </mesh>
          {/* Even lighter brown stripes */}
          <mesh position={[0, 0, 0.001]}>
            <planeGeometry args={[15, 200]} />
            <meshStandardMaterial 
              color="#E6CCB2"
              opacity={0.5}
              transparent
              alphaMap={new THREE.DataTexture(
                Uint8Array.from([0, 255, 0, 255, 0, 255, 0, 255]),
                2,
                4,
                THREE.LuminanceFormat
              )}
            />
          </mesh>
        </group>
        
        <Car ref={carRef} position={[0, 0, -90]} rotation={[0, Math.PI, 0]} />
        <CameraRig target={carRef} orbitMode={orbitMode} />
        {orbitMode && <OrbitControls />}
      </Canvas>
    </div>
  );
}
